import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedIcon from '@mui/icons-material/Verified';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { Link } from 'react-router-dom';

const CmmiTraining: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const trainingCourses = [
    {
      title: "Introduction to CMMI",
      description: "Learn the fundamentals of CMMI, including its history, structure, and key concepts.",
      duration: "1 day",
      audience: "All stakeholders",
      benefits: [
        "Understand the CMMI model structure",
        "Learn basic CMMI terminology",
        "Identify how CMMI can benefit your organization"
      ]
    },
    {
      title: "CMMI for Practitioners",
      description: "Detailed training on implementing CMMI practices within your functional area.",
      duration: "2 days",
      audience: "Team leads, developers, QA, and other practitioners",
      benefits: [
        "Apply CMMI concepts to your daily work",
        "Implement process improvement initiatives",
        "Participate effectively in appraisals"
      ]
    },
    {
      title: "CMMI for Process Engineers",
      description: "Advanced training focusing on defining and improving processes aligned with CMMI.",
      duration: "3 days",
      audience: "Process engineers and improvement specialists",
      benefits: [
        "Design processes aligned with CMMI practices",
        "Create effective process documentation",
        "Implement measurement and analysis systems"
      ]
    },
    {
      title: "CMMI for Executives",
      description: "Strategic overview of CMMI and its business benefits for organizational leaders.",
      duration: "Half-day",
      audience: "Executives and senior management",
      benefits: [
        "Understand business value of CMMI",
        "Make informed decisions about process improvement",
        "Set realistic goals for CMMI implementation"
      ]
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          py: 12,
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom 
              fontWeight="bold"
              sx={{ mb: 3 }}
            >
              CMMI Training
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                maxWidth: '800px', 
                mb: 5,
                opacity: 0.9
              }}
            >
              Comprehensive CMMI training delivered by certified CMMI instructors to help your team understand and implement CMMI practices effectively.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact?service=cmmi-training"
                sx={{
                  bgcolor: 'white',
                  color: '#0c2340',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)',
                  },
                  px: 4,
                  py: 1.5,
                  borderRadius: '8px',
                  fontWeight: 'bold'
                }}
              >
                Request Training
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/services/cmmi"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                  px: 4,
                  py: 1.5,
                  borderRadius: '8px'
                }}
              >
                View All CMMI Services
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Training Overview
              </Typography>
              <Typography paragraph>
                Our CMMI training programs are designed to help organizations understand and implement CMMI practices effectively. Whether you're new to CMMI or looking to enhance your expertise, our comprehensive training courses cover all aspects of the CMMI model.
              </Typography>
              <Typography paragraph>
                All of our training courses are delivered by certified CMMI instructors with extensive practical experience in implementing CMMI across various industries. Our instructors combine theoretical knowledge with real-world examples to provide participants with actionable insights they can apply immediately in their organizations.
              </Typography>
              <Typography paragraph>
                We offer both standard and customized training options to meet the specific needs of your organization. Training can be delivered on-site at your location or virtually, depending on your preference.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="CMMI Training Session"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Training Courses Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Available Training Courses
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              We offer a variety of CMMI training courses tailored to different roles and experience levels within your organization.
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(2, 1fr)' 
            },
            gap: 4 
          }}>
            {trainingCourses.map((course, index) => (
              <Box key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="h5"
                        component="h3"
                        gutterBottom
                        fontWeight="bold"
                        color="#0c2340"
                      >
                        {course.title}
                      </Typography>
                      <Typography color="text.secondary" paragraph>
                        {course.description}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
                        <Box>
                          <Typography variant="subtitle2" color="primary" fontWeight="bold">
                            Duration
                          </Typography>
                          <Typography variant="body2">
                            {course.duration}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="primary" fontWeight="bold">
                            Target Audience
                          </Typography>
                          <Typography variant="body2">
                            {course.audience}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mt: 2 }}>
                        Key Benefits
                      </Typography>
                      <List dense>
                        {course.benefits.map((benefit, idx) => (
                          <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircleOutlineIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={benefit} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Training Approach Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                Our Training Approach
              </Typography>
              <Typography paragraph>
                Our training approach combines theoretical knowledge with practical application to ensure participants gain a deep understanding of CMMI concepts and how to apply them in their daily work.
              </Typography>
              
              <List>
                {[
                  "Interactive sessions with real-world examples and case studies",
                  "Hands-on exercises to reinforce learning",
                  "Customized content aligned with your organization's goals",
                  "Post-training support to assist with implementation",
                  "Continuous learning resources to keep your team updated"
                ].map((item, index) => (
                  <ListItem key={index} disablePadding sx={{ mb: 2 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 5,
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Customized Training Programs
                </Typography>
                <Typography paragraph sx={{ opacity: 0.9, mb: 4 }}>
                  We understand that every organization is unique. That's why we offer customized training programs tailored to your specific needs and challenges.
                </Typography>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 3 }} />
                <Typography fontWeight="medium" gutterBottom>
                  Our customization options include:
                </Typography>
                <List sx={{ mb: 3 }}>
                  {[
                    'Industry-specific examples and case studies',
                    'Focus on specific CMMI process areas relevant to your business',
                    'Integration with your existing processes and tools',
                    'Training for specific roles within your organization',
                    'Flexible scheduling to minimize disruption'
                  ].map((item, idx) => (
                    <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36, color: 'white' }}>
                        <CheckCircleOutlineIcon sx={{ color: '#90caf9' }} />
                      </ListItemIcon>
                      <ListItemText primary={item} sx={{ color: 'white' }} />
                    </ListItem>
                  ))}
                </List>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    bgcolor: 'white',
                    color: '#0c2340',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                    },
                    px: 3,
                    py: 1.5,
                    borderRadius: '8px',
                    mt: 2
                  }}
                  component={Link}
                  to="/contact?service=cmmi-training"
                >
                  Request Custom Training
                </Button>
              </Paper>
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Training Stats Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
            >
              Training Impact
            </Typography>
            <Typography 
              textAlign="center" 
              sx={{ 
                maxWidth: 800, 
                mx: 'auto', 
                mb: 6 
              }}
            >
              Our CMMI training programs have delivered significant value to organizations across various industries.
            </Typography>
          </motion.div>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(4, 1fr)' 
            },
            gap: 4 
          }}>
            {[
              {
                statistic: "95%",
                description: "Participant satisfaction rating across all CMMI training courses",
                icon: <SchoolIcon fontSize="large" />
              },
              {
                statistic: "10,000+",
                description: "Professionals trained in CMMI best practices over the past decade",
                icon: <PeopleIcon fontSize="large" />
              },
              {
                statistic: "92%",
                description: "Of organizations report improved process performance after training",
                icon: <VerifiedIcon fontSize="large" />
              },
              {
                statistic: "85%",
                description: "Of clients achieved their target CMMI maturity level within 18 months",
                icon: <EmojiEventsIcon fontSize="large" />
              }
            ].map((item, index) => (
              <Box key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      textAlign: 'center',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 70,
                        height: 70,
                        borderRadius: '50%',
                        backgroundColor: '#1976d215',
                        color: '#1976d2',
                        mb: 3
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      variant="h3"
                      component="p"
                      fontWeight="bold"
                      color="#0c2340"
                      sx={{ mb: 2 }}
                    >
                      {item.statistic}
                    </Typography>
                    <Typography color="text.secondary">
                      {item.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ 
        backgroundColor: 'white', 
        py: 12,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                position: 'relative'
              }}
            >
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Ready to Empower Your Team?
              </Typography>
              <Typography 
                variant="body1" 
                paragraph 
                sx={{ 
                  maxWidth: '700px', 
                  mx: 'auto', 
                  mb: 4, 
                  opacity: 0.9 
                }}
              >
                Our expert trainers are ready to equip your team with the knowledge and skills they need to implement CMMI effectively. Contact us today to discuss your training needs.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/contact?service=cmmi-training"
                  sx={{
                    bgcolor: 'white',
                    color: '#0c2340',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }}
                >
                  Schedule Training
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/services/cmmi"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px'
                  }}
                >
                  Back to CMMI Services
                </Button>
              </Box>

              {/* Decorative circles */}
              <Box sx={{
                position: 'absolute',
                top: -30,
                left: -30,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
              <Box sx={{
                position: 'absolute',
                bottom: -40,
                right: -40,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default CmmiTraining; 